import { ComponentType, lazy, LazyExoticComponent, SVGProps } from "react";
import {
  AdjustmentsHorizontalIcon,
  ArchiveBoxIcon,
  ArrowTrendingUpIcon,
  ArrowUturnLeftIcon,
  BanknotesIcon,
  BookOpenIcon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  CameraIcon,
  ChartBarIcon,
  CheckBadgeIcon,
  CircleStackIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  CurrencyPoundIcon,
  DocumentChartBarIcon,
  DocumentTextIcon,
  HandRaisedIcon,
  IdentificationIcon,
  InboxStackIcon,
  LockClosedIcon,
  MapIcon,
  MapPinIcon,
  PhoneArrowUpRightIcon,
  PhotoIcon,
  RectangleGroupIcon,
  ScaleIcon,
  ShieldCheckIcon,
  ShoppingCartIcon,
  SparklesIcon,
  SwatchIcon,
  TagIcon,
  TruckIcon,
  UserGroupIcon,
  UsersIcon,
  ViewColumnsIcon,
  WrenchScrewdriverIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

/**
 * Categories are rendered on the Help index in this order. A category with no
 * visible articles is dropped entirely, so customers only see the sections that
 * apply to them.
 */
export const HELP_CATEGORIES = [
  "Getting Started",
  "Leads & Collections",
  "Payments",
  "Accounts & Invoicing",
  "Vehicles & Dismantling",
  "Production",
  "Parts & Stock",
  "Order Management",
  "Reporting",
  "Admin",
] as const;

export type HelpCategory = (typeof HELP_CATEGORIES)[number];

export interface HelpArticle {
  slug: string;
  title: string;
  description: string;
  category: HelpCategory;
  /**
   * The dashboard route this article documents, without a leading slash. Drives
   * two things: role-scope gating (via featureForRoutePath) and the contextual
   * Help button in the topbar. Leave undefined for articles that aren't tied to
   * a single page.
   */
  route?: string;
  /** Extra routes that should surface this article's contextual Help button. */
  altRoutes?: string[];
  /**
   * Customer feature flag name. Presence-based, matching how the sidebar gates
   * its items in DashboardLayout — a Disabled feature still counts as present.
   */
  featureFlag?: string;
  /** Hidden from anyone without the isAdmin role claim. */
  adminOnly?: boolean;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  component: LazyExoticComponent<ComponentType<any>>;
}

/**
 * The single source of truth for the Help section: the index page, the router
 * children and the contextual Help button are all generated from this array, so
 * adding an article is a one-line change plus a component file.
 */
export const helpArticles: HelpArticle[] = [
  // ---------------------------------------------------------------- Getting Started
  {
    slug: "getting-started",
    title: "Finding your way around Wilma",
    description:
      "The sidebar, the search bar, notifications and where to go when you get stuck.",
    category: "Getting Started",
    icon: BookOpenIcon,
    component: lazy(() => import("./articles/GettingStarted")),
  },
  {
    slug: "management-dashboard",
    title: "Reading the Dashboard",
    description:
      "Yard inventory, processing queues, what came off the line and how long each station is taking.",
    category: "Getting Started",
    // No route: "" — the dashboard is the index route, and an empty prefix would gate this
    // article on the dashboard scope in a way featureForRoutePath handles differently from a
    // named path. It is gated on the TME flag instead, which is what makes the page appear.
    featureFlag: "TME",
    icon: ChartBarIcon,
    component: lazy(() => import("./articles/ManagementDashboardGuide")),
  },

  // ---------------------------------------------------------- Leads & Collections
  {
    slug: "creating-a-lead",
    title: "Creating a lead",
    description:
      "Looking up a vehicle, finding the seller's address, agreeing a price and booking a collection or drop off.",
    category: "Leads & Collections",
    route: "leads/create",
    altRoutes: ["leads/create-multiple", "bookings/create"],
    featureFlag: "Lead Management",
    icon: ClipboardDocumentListIcon,
    component: lazy(() => import("./articles/CreatingALead")),
  },
  //Listed before importing-leads deliberately: both claim the "leads" route, and
  //the contextual Help button resolves a tie to whichever comes first, so the
  //lifecycle guide has to win on a lead page.
  {
    slug: "lead-flow",
    title: "The lead lifecycle, start to finish",
    description:
      "Every stage a lead passes through — scheduling, check in, collection, payment and completion — and what to press at each one.",
    category: "Leads & Collections",
    route: "leads",
    altRoutes: ["bookings", "vehicles-collected", "leads/deleted"],
    featureFlag: "Lead Management",
    icon: ArrowTrendingUpIcon,
    component: lazy(() => import("./articles/LeadFlow")),
  },
  {
    slug: "importing-leads",
    title: "Importing and exporting leads by CSV",
    description: "Bringing a batch of leads in from a spreadsheet, and getting your leads back out.",
    category: "Leads & Collections",
    route: "leads",
    featureFlag: "Lead Management",
    icon: CloudArrowUpIcon,
    component: lazy(() => import("./articles/ImportingLeads")),
  },
  {
    slug: "quotes-call-list",
    title: "Working the quote call list",
    description: "Calling through quotes, moving them along the stages and turning them into bookings.",
    category: "Leads & Collections",
    route: "quotes",
    featureFlag: "Call Centre",
    icon: PhoneArrowUpRightIcon,
    component: lazy(() => import("./articles/QuotesCallList")),
  },
  {
    slug: "trip-manager",
    title: "Planning collections in Trip Manager",
    description:
      "Building trips, assigning drivers and trucks, optimising the route and printing collection notes.",
    category: "Leads & Collections",
    route: "trip-manager",
    altRoutes: ["old-trip-manager", "calendar"],
    featureFlag: "Lead Management",
    icon: MapIcon,
    component: lazy(() => import("./articles/TripManagerGuide")),
  },
  {
    slug: "drop-off-manager",
    title: "Managing drop offs",
    description: "Booking sellers into drop off slots, clearing a day and printing the daily summary.",
    category: "Leads & Collections",
    route: "drop-offs",
    featureFlag: "Lead Management",
    icon: TruckIcon,
    component: lazy(() => import("./articles/DropOffManager")),
  },
  {
    slug: "schedule-job-sheets",
    title: "Schedule and job sheets",
    description: "Checking what's booked in for a day and printing job sheets for your drivers.",
    category: "Leads & Collections",
    route: "schedule",
    icon: CalendarDaysIcon,
    component: lazy(() => import("./articles/ScheduleJobSheets")),
  },

  // ------------------------------------------------------------------------ Payments
  {
    slug: "payments",
    title: "Approving and tracking payments",
    description:
      "Checking the balance, approving what's waiting, chasing failures and understanding each payment status.",
    category: "Payments",
    route: "payments",
    featureFlag: "Customer Payments",
    icon: BanknotesIcon,
    component: lazy(() => import("./articles/PaymentsGuide")),
  },

  // ------------------------------------------------------- Accounts & Invoicing
  {
    slug: "accounts",
    title: "Accounts, contacts and addresses",
    description: "Setting up trade accounts, adding contacts and addresses, and reading their history.",
    category: "Accounts & Invoicing",
    route: "accounts",
    altRoutes: ["contacts"],
    icon: UserGroupIcon,
    component: lazy(() => import("./articles/AccountsGuide")),
  },
  {
    slug: "invoicing",
    title: "Raising and managing invoices",
    description:
      "Generating an invoice, editing line items, crediting a mistake, and exporting the lot to Xero or Oracle.",
    category: "Accounts & Invoicing",
    route: "invoices",
    icon: DocumentTextIcon,
    component: lazy(() => import("./articles/InvoicingGuide")),
  },
  {
    slug: "vat-margin-scheme",
    title: "Using the VAT Margin Scheme",
    description:
      "How Global Accounting works in Wilma — recording purchase VAT, selling parts, invoices, and the period report.",
    category: "Accounts & Invoicing",
    icon: CurrencyPoundIcon,
    component: lazy(() => import("./articles/VatMarginSchemeGuide")),
  },

  // ------------------------------------------------- Vehicles & Dismantling
  {
    slug: "vehicle-flow",
    title: "The vehicle lifecycle and Vehicle Board",
    description:
      "How a vehicle moves from purchased to dismantled, and how to drive it from the board.",
    category: "Vehicles & Dismantling",
    route: "vehicle-board",
    altRoutes: ["vehicles"],
    icon: ViewColumnsIcon,
    component: lazy(() => import("./articles/VehicleFlow")),
  },
  {
    slug: "awaiting-inventory",
    title: "Awaiting Inventory",
    description: "Vehicles that have arrived but haven't had their parts booked in yet.",
    category: "Vehicles & Dismantling",
    route: "parts-manager",
    icon: InboxStackIcon,
    component: lazy(() => import("./articles/AwaitingInventoryGuide")),
  },
  {
    slug: "awaiting-arrival",
    title: "Vehicles awaiting arrival",
    description: "Keeping an eye on what's due into the yard today.",
    category: "Vehicles & Dismantling",
    route: "awaiting-arrival",
    icon: TruckIcon,
    component: lazy(() => import("./articles/AwaitingArrivalGuide")),
  },
  {
    slug: "depollution",
    title: "Running a depollution",
    description:
      "Starting a depollution from a reg, working the checklist, recording quantities and signing it off.",
    category: "Vehicles & Dismantling",
    route: "depollution",
    icon: ShieldCheckIcon,
    component: lazy(() => import("./articles/DepollutionGuide")),
  },
  // Deliberately no route: the check-in tasks list is a modal, not a page, so there is nowhere for
  // a contextual Help button to sit. That also leaves the article ungated, which is right — anyone
  // who runs into a padlock needs to be able to read why, including the people who can't open it.
  {
    slug: "vehicle-complexity",
    title: "Vehicle complexity rules",
    description:
      "Grading vehicles by make and model, how the most specific rule wins, and copying a grading from another site.",
    category: "Vehicles & Dismantling",
    route: "vehicle-complexity",
    featureFlag: "TME",
    adminOnly: true,
    icon: ScaleIcon,
    component: lazy(() => import("./articles/VehicleComplexityGuide")),
  },
  {
    slug: "protected-documents",
    title: "Protected documents",
    description:
      "Why the V5C is locked, who can open it, the PIN prompt, the record of who has looked, and click-to-view identity documents.",
    category: "Vehicles & Dismantling",
    icon: LockClosedIcon,
    component: lazy(() => import("./articles/ProtectedDocumentsGuide")),
  },
  {
    slug: "triage",
    title: "Running a triage",
    description: "Assessing a vehicle before it reaches the line and recording what you find.",
    category: "Vehicles & Dismantling",
    route: "triage",
    featureFlag: "Triage",
    icon: CircleStackIcon,
    component: lazy(() => import("./articles/TriageGuide")),
  },
  // Not in the Admin category on purpose: the audience is the operational users who sign, and
  // everything under Admin is adminOnly. Gated instead on the cod scope via the "profile" route,
  // with the admin COD page as an alt route so an administrator setting up the business signature
  // gets the same contextual Help button.
  {
    slug: "cod-signatures",
    title: "Signing Certificates of Destruction",
    description:
      "Setting up your own signature, the two switches that have to be on, and checking who signed a certificate.",
    category: "Vehicles & Dismantling",
    route: "profile",
    altRoutes: ["admin/cod"],
    icon: IdentificationIcon,
    component: lazy(() => import("./articles/CodSignaturesGuide")),
  },
  {
    slug: "dismantling-jobs",
    title: "Working a dismantling job",
    description:
      "The dismantling plan, marking parts off, assembly timers, scanning, labels and completing the job.",
    category: "Vehicles & Dismantling",
    route: "jobs",
    icon: WrenchScrewdriverIcon,
    component: lazy(() => import("./articles/DismantlingJobs")),
  },

  // ---------------------------------------------------------------------- Production
  {
    slug: "production-planner",
    title: "Planning the line in Production Planner",
    description:
      "Allocating vehicles to bays and days, managing capacity, rebooking and clearing a plan.",
    category: "Production",
    route: "production-planner",
    featureFlag: "Production Manager",
    icon: RectangleGroupIcon,
    component: lazy(() => import("./articles/ProductionPlannerGuide")),
  },
  {
    slug: "production-dashboard",
    title: "Reading the Production Dashboard",
    description: "The wallboard view: what every number and station card is telling you.",
    category: "Production",
    route: "production-dashboard",
    featureFlag: "Production Manager",
    icon: ChartBarIcon,
    component: lazy(() => import("./articles/ProductionDashboardGuide")),
  },
  {
    slug: "station-manager",
    title: "Using Station Manager at a bay",
    description: "Picking your station and scanning a vehicle in to start work.",
    category: "Production",
    route: "station-manager",
    featureFlag: "Production Manager",
    icon: AdjustmentsHorizontalIcon,
    component: lazy(() => import("./articles/StationManagerGuide")),
  },

  // ------------------------------------------------------------------ Parts & Stock
  {
    slug: "work-orders",
    title: "Work orders",
    description:
      "Raising a work order, adding parts to it from Stock or Orders, and working it through to complete.",
    category: "Parts & Stock",
    route: "work-orders",
    icon: ClipboardDocumentCheckIcon,
    component: lazy(() => import("./articles/WorkOrdersGuide")),
  },
  {
    slug: "exports",
    title: "Vehicle exports and offers",
    description: "Assigning an exporter and recording engine and VFE offers against a vehicle.",
    category: "Parts & Stock",
    route: "exports",
    icon: BuildingStorefrontIcon,
    component: lazy(() => import("./articles/ExportsGuide")),
  },
  {
    slug: "stock",
    title: "The Stock page",
    description:
      "Filtering with the stat tiles, finding problem parts and using the bulk actions safely.",
    category: "Parts & Stock",
    route: "stock",
    featureFlag: "Parts Tracker",
    icon: ArchiveBoxIcon,
    component: lazy(() => import("./articles/StockGuide")),
  },
  {
    slug: "stock-part-detail",
    title: "Working a single part",
    description: "The part page tabs — information, listing, images and buyer account — and what you can do from each.",
    category: "Parts & Stock",
    route: "stock",
    featureFlag: "Parts Tracker",
    icon: TagIcon,
    component: lazy(() => import("./articles/StockPartDetail")),
  },
  {
    slug: "quality-control",
    title: "Quality Control",
    description: "Sending parts to QC, working the QC queue, and the Deleted and Scrap Out tabs.",
    category: "Parts & Stock",
    route: "stock-qc",
    featureFlag: "Parts Tracker",
    icon: CheckBadgeIcon,
    component: lazy(() => import("./articles/QualityControlGuide")),
  },
  {
    slug: "part-requests",
    title: "Part Requests",
    description:
      "PartPro buyer requests matched against your yard, claiming and messaging buyers, logging internal requests and fulfilling them on the network.",
    category: "Parts & Stock",
    route: "part-requests",
    featureFlag: "Part Requests",
    icon: HandRaisedIcon,
    component: lazy(() => import("./articles/PartRequestsGuide")),
  },
  {
    slug: "parts-checker",
    title: "Parts Checker",
    description: "Looking up which part numbers fit a vehicle.",
    category: "Parts & Stock",
    route: "parts-checker",
    featureFlag: "Parts Lookup",
    icon: SwatchIcon,
    component: lazy(() => import("./articles/PartsCheckerGuide")),
  },
  {
    slug: "rs-core",
    title: "RS Core",
    description: "Sending stock through to RS Core, and why the menu item is sometimes greyed out.",
    category: "Parts & Stock",
    route: "core",
    featureFlag: "Parts Tracker",
    icon: CircleStackIcon,
    component: lazy(() => import("./articles/RsCoreGuide")),
  },

  // ------------------------------------------------------------- Order Management
  {
    slug: "unlisted-parts",
    title: "Getting parts ready to list",
    description:
      "Reading the readiness columns, fixing what's missing, applying defaults and running a bulk list.",
    category: "Order Management",
    route: "ebay-unlisted",
    altRoutes: ["ebay-listings/bulk-list"],
    featureFlag: "eBay Management",
    icon: SparklesIcon,
    component: lazy(() => import("./articles/UnlistedParts")),
  },
  {
    slug: "active-listings",
    title: "Managing active listings",
    description:
      "Relisting, withdrawing, pushing title and policy updates, and using intelligence-led pricing.",
    category: "Order Management",
    route: "ebay-listings",
    featureFlag: "eBay Management",
    icon: BuildingStorefrontIcon,
    component: lazy(() => import("./articles/ActiveListings")),
  },
  {
    slug: "recently-listed",
    title: "Recently Listed",
    description: "Checking the last 90 days of listings and clearing up listing errors.",
    category: "Order Management",
    route: "recently-listed",
    featureFlag: "eBay Management",
    icon: CalendarDaysIcon,
    component: lazy(() => import("./articles/RecentlyListedGuide")),
  },
  {
    slug: "listing-editor",
    title: "Editing a part's listing",
    description: "Where the listing editor lives now, and what each section of it controls.",
    category: "Order Management",
    route: "stock",
    featureFlag: "eBay Management",
    icon: DocumentTextIcon,
    component: lazy(() => import("./articles/ListingEditor")),
  },
  {
    slug: "listing-optimisation",
    title: "Listing Optimisation",
    description: "The five-step flow for finding, staging and pushing pricing and content improvements.",
    category: "Order Management",
    route: "ebay-optimisation",
    featureFlag: "eBay Optimisation",
    icon: ArrowTrendingUpIcon,
    component: lazy(() => import("./articles/ListingOptimisationGuide")),
  },
  {
    slug: "orders",
    title: "Picking and shipping orders",
    description:
      "Working the pick list, creating and printing shipping labels, and marking orders as shipped.",
    category: "Order Management",
    route: "ebay-orders",
    featureFlag: "eBay Management",
    icon: ShoppingCartIcon,
    component: lazy(() => import("./articles/OrdersGuide")),
  },
  {
    slug: "cancelling-an-order",
    title: "Cancelling an order",
    description: "When you can cancel, the reason you should pick, and what to do when you can't.",
    category: "Order Management",
    route: "stock",
    featureFlag: "eBay Management",
    icon: XCircleIcon,
    component: lazy(() => import("./articles/CancellingAnOrder")),
  },
  {
    slug: "returns",
    title: "Handling eBay returns",
    description:
      "Responding in time, reading the dispute, approving a return and marking the refund as sent.",
    category: "Order Management",
    route: "ebay-returns",
    featureFlag: "eBay Management",
    icon: ArrowUturnLeftIcon,
    component: lazy(() => import("./articles/ReturnsGuide")),
  },

  // ----------------------------------------------------------------------- Reporting
  {
    slug: "reporting",
    title: "Reports and performance tracking",
    description:
      "Where reporting lives, who can see it, running a report and reading personnel activity.",
    category: "Reporting",
    // Gates the article on the reporting scope automatically, via featureForRoutePath.
    route: "reports",
    altRoutes: ["performance"],
    icon: DocumentChartBarIcon,
    component: lazy(() => import("./articles/ReportingGuide")),
  },

  // --------------------------------------------------------------------------- Admin
  {
    slug: "category-editor",
    title: "Category Editor and eBay titles",
    description:
      "Building title structures, setting listing defaults and aspects, and mapping incoming part names.",
    category: "Admin",
    route: "category-editor",
    adminOnly: true,
    icon: TagIcon,
    component: lazy(() => import("./articles/CategoryEditorGuide")),
  },
  {
    slug: "bulk-category-defaults",
    title: "Bulk updating default categories and policies",
    description:
      "Pushing a store category, shipping/payment/returns policy or title structure across many categories at once.",
    category: "Admin",
    route: "category-editor",
    adminOnly: true,
    icon: RectangleGroupIcon,
    component: lazy(() => import("./articles/BulkCategoryDefaults")),
  },
  {
    slug: "required-images",
    title: "Required images for the app's photo flow",
    description:
      "Defining the photo checklist the mobile app prompts for, per category, and reusing image groups.",
    category: "Admin",
    route: "category-images",
    adminOnly: true,
    icon: CameraIcon,
    component: lazy(() => import("./articles/RequiredImages")),
  },
  {
    slug: "part-image-settings",
    title: "Part image processing settings",
    description:
      "Background removal, watermarks, image ordering and how many photos to keep.",
    category: "Admin",
    route: "admin/part-image-settings",
    adminOnly: true,
    icon: PhotoIcon,
    component: lazy(() => import("./articles/PartImageSettings")),
  },
  {
    slug: "roles",
    title: "Roles and feature access",
    description:
      "How roles, base role types and per-feature None/Read/Write levels decide what each person sees.",
    category: "Admin",
    route: "admin/roles",
    adminOnly: true,
    icon: IdentificationIcon,
    component: lazy(() => import("./articles/RolesGuide")),
  },
  {
    slug: "staff",
    title: "Staff accounts",
    description: "Adding a staff member, setting their role and resetting a password.",
    category: "Admin",
    route: "admin/staff",
    adminOnly: true,
    icon: UsersIcon,
    component: lazy(() => import("./articles/StaffGuide")),
  },
  {
    slug: "locations",
    title: "Locations, yard sections and warehouse locations",
    description: "The three levels of location in Wilma and which one you actually want.",
    category: "Admin",
    route: "admin/locations",
    adminOnly: true,
    icon: MapPinIcon,
    component: lazy(() => import("./articles/LocationsGuide")),
  },
  {
    slug: "part-bins",
    title: "Part bins",
    description: "Setting up the bins parts get picked into, and printing their QR labels.",
    category: "Admin",
    route: "admin/part-bins",
    adminOnly: true,
    icon: ArchiveBoxIcon,
    component: lazy(() => import("./articles/PartBinsGuide")),
  },
  {
    slug: "shipping-providers",
    title: "Shipping providers",
    description: "Connecting a courier so Wilma can buy labels for you.",
    category: "Admin",
    route: "admin/shipping-providers",
    adminOnly: true,
    icon: TruckIcon,
    component: lazy(() => import("./articles/ShippingProvidersGuide")),
  },
  {
    slug: "shipping-policies",
    title: "Shipping policies",
    description: "Creating the policies your listings use, and syncing them to PartPro and Shopify.",
    category: "Admin",
    route: "admin/shipping-policies",
    adminOnly: true,
    icon: Cog6ToothIcon,
    component: lazy(() => import("./articles/ShippingPoliciesGuide")),
  },
  {
    slug: "warranty-manager",
    title: "Warranty manager",
    description: "Setting up the warranty options your parts can be sold with.",
    category: "Admin",
    route: "admin/warranty-manager",
    adminOnly: true,
    icon: ShieldCheckIcon,
    component: lazy(() => import("./articles/WarrantyManagerGuide")),
  },
];

/**
 * Longest-prefix match of a pathname to an article, for the contextual Help
 * button. Several articles may legitimately claim the same route (Stock has
 * four); an equal-length tie resolves to the earliest one in the registry, so
 * the primary guide for an area must be listed first.
 */
export function helpArticleForRoute(
  pathname: string,
  articles: HelpArticle[] = helpArticles
): HelpArticle | undefined {
  const normalised = pathname.replace(/^\//, "").replace(/\/$/, "");
  if (normalised === "") return undefined;

  let best: HelpArticle | undefined;
  let bestLength = -1;

  for (const article of articles) {
    const candidates = [article.route, ...(article.altRoutes ?? [])].filter(
      (route): route is string => !!route
    );

    for (const candidate of candidates) {
      const matches =
        normalised === candidate || normalised.startsWith(`${candidate}/`);
      if (matches && candidate.length > bestLength) {
        best = article;
        bestLength = candidate.length;
      }
    }
  }

  return best;
}
